// classe singleton che gestisce lo stato di autenticazione dell'utente
// e fornisce metodi per il login, logout e verifica dello stato di autenticazione
import type {
    TotpSetupResponse,
} from '@bonusmap/api-types';
import {
    apiError,
    apiSuccess,
    loginResponseDataSchema,
    type LoginResponseData,
    type TotpVerifyInput,
    type LoginWithTotpInput,
    type LoginWithTotpResponse,
    type RegenerateBackupCodesInput,
    type RegenerateBackupCodesResponse
} from './Schemes.js';
import type {
    User,
    ApiResponse,
    TotpVerifyResponse,
    ForgotPasswordResponse,
    ResetPasswordResponse,
    VerifyResetTokenResponse
} from '@bonusmap/api-types';
type LoginFlow = 'login'| 'authenticated' |'setup' | 'code' | null
type AuthState =  'loading' | 'password_recovery' | 'authenticated' | 'unauthenticated' | 'pending_totp'

class AuthStore {
    private static instance: AuthStore;

    // Stato autenticazione base
    user: Partial<User> | null = null;
    token: null | string = null;
    cookieName: null | string = null;
    private _authState: AuthState = 'loading';
    public get authState(): AuthState {
        return this._authState;
    }
    public set authState(value: AuthState) {
        this._authState = value;
       
    }

    // Flusso TOTP
    loginFlow: null | LoginFlow = null;
    tempToken: null | string = null;
    setupToken: null | string = null;
    // Dati TOTP temporanei
    totpSecret: null | string = null;
    qrCodeDataUrl: null | string = null;
    backupCodes: null | string[] = null;
    // Stato UI
    isLoading: boolean = false;
    lastError: string | null = null;
    // Getter base
    get isAuthenticated(): boolean {
        return this.authState === 'authenticated';
    }
    get isFullyAuthenticated(): boolean {
        return this.isAuthenticated && !this.loginFlow;
    }
    get isPendingTotp(): boolean {
        return this.authState === 'pending_totp' && this.loginFlow !== null;
    }
    get isPasswordRecovery (): boolean {
        return this.authState === 'password_recovery';
    }
    // Getter flusso TOTP
    get requiresTotpSetup(): boolean {
        return this.loginFlow === "setup" && this.setupToken !== null;
    }
    get requiresTotpCode(): boolean {
        return this.loginFlow === 'code' && this.tempToken !== null
    }
    // Getter dati temporanei
    get hasBackupCodes(): boolean {
        return this.backupCodes !== null && this.backupCodes.length > 0
    }

    // Getter stato UI
    get canProceedWithSetup(): boolean {
        return this.totpSecret !== null && this.qrCodeDataUrl !== null
    }

    get currentFlowStep(): LoginFlow {
        if (this.requiresTotpSetup) return 'setup'
        if (this.requiresTotpCode) return 'code'
        if (this.isAuthenticated) return 'authenticated'
        return 'login'
    }
    private constructor() { }

    public static getInstance(): AuthStore {
        if (!AuthStore.instance) {
            AuthStore.instance = new AuthStore();
        }
        return AuthStore.instance;
    }

    // ===== GESTIONE STATO CENTRALIZZATA =====
    changeState(settings: {
        authState?: AuthState;
        loginFlow?: LoginFlow;
        setupToken?: string | null;
        tempToken?: string | null;
        backupCodes?: string[] | null;
        totpSecret?: string | null;
        qrCodeDataUrl?: string | null;
        user?: Partial<User> | null;
        token?: string | null;
        cookieName?: string | null;
    }): void {
    const isAuthenticated = this.isAuthenticated;
    const isFullyAuthenticated = this.isFullyAuthenticated;
    const isPendingTotp = this.isPendingTotp;
    const isPasswordRecovery = this.isPasswordRecovery;
    const requiresTotpSetup = this.requiresTotpSetup;
    const requiresTotpCode = this.requiresTotpCode;
    const hasBackupCodes = this.hasBackupCodes;
    const canProceedWithSetup = this.canProceedWithSetup;
        if (settings.authState !== undefined) {
            this.authState = settings.authState;
        }
        if (settings.loginFlow !== undefined) {
            this.loginFlow = settings.loginFlow;
        }
        if (settings.setupToken !== undefined) {
            this.setupToken = settings.setupToken;
        }
        if (settings.tempToken !== undefined) {
            this.tempToken = settings.tempToken;
        }
        if (settings.backupCodes !== undefined) {
            this.backupCodes = settings.backupCodes;
        }
        if (settings.totpSecret !== undefined) {
            this.totpSecret = settings.totpSecret;
        }
        if (settings.qrCodeDataUrl !== undefined) {
            this.qrCodeDataUrl = settings.qrCodeDataUrl;
        }
        if (settings.user !== undefined) {
            this.user = settings.user;
        }
        if (settings.token !== undefined) {
            this.token = settings.token;
        }
        if (settings.cookieName !== undefined) {
            this.cookieName = settings.cookieName;
        }
        if(isAuthenticated !== this.isAuthenticated) {
            window.dispatchEvent(new CustomEvent('auth-is-authenticated-changed',{detail:{newValue:this.isAuthenticated, oldValue:isAuthenticated}}));
        }
        if(isFullyAuthenticated !== this.isFullyAuthenticated) {
            window.dispatchEvent(new CustomEvent('auth-is-fully-authenticated-changed',{detail:{newValue:this.isFullyAuthenticated, oldValue:isFullyAuthenticated}}));
        }
        if(isPendingTotp !== this.isPendingTotp) {
            window.dispatchEvent(new CustomEvent('auth-is-pending-totp-changed',{detail:{newValue:this.isPendingTotp, oldValue:isPendingTotp}}));
        }
        if(isPasswordRecovery !== this.isPasswordRecovery) {
            window.dispatchEvent(new CustomEvent('auth-is-password-recovery-changed',{detail:{newValue:this.isPasswordRecovery, oldValue:isPasswordRecovery}}));
        }
        if(requiresTotpSetup !== this.requiresTotpSetup) {
            window.dispatchEvent(new CustomEvent('auth-requires-totp-setup-changed',{detail:{newValue:this.requiresTotpSetup, oldValue:requiresTotpSetup}}));
        }
        if(requiresTotpCode !== this.requiresTotpCode) {
            window.dispatchEvent(new CustomEvent('auth-requires-totp-code-changed',{detail:{newValue:this.requiresTotpCode, oldValue:requiresTotpCode}}));
        }
        if(hasBackupCodes !== this.hasBackupCodes) {
            window.dispatchEvent(new CustomEvent('auth-has-backup-codes-changed',{detail:{newValue:this.hasBackupCodes, oldValue:hasBackupCodes}}));
        }
        if(canProceedWithSetup !== this.canProceedWithSetup) {
            window.dispatchEvent(new CustomEvent('auth-can-proceed-with-setup-changed',{detail:{newValue:this.canProceedWithSetup, oldValue:canProceedWithSetup}}));    
        }
         const eventName = `auth-state-changed`;
            window.dispatchEvent(new Event(eventName));
        }
    private $reset() {
        // riporta tutto allo stato iniziale
        this.changeState({
            user: null,
            token: null,
            backupCodes: null,
            loginFlow: null,
            authState: "unauthenticated"
        });
    }
    // ===== AZIONI BASE =====
    getUser(): Partial<User> | null {
        return this.user
    }
    setLoading(loading: boolean) {
        this.isLoading = loading
        this.lastError = null
    }
    private setError(error: string) {
        this.lastError = error
        this.isLoading = false
    }
    private clearError() {
        this.lastError = null
    }
    // ===== RESET E LOGOUT =====

    resetAuth() {
        // Pulisci cookie se esiste
        if (this.cookieName) {
            window.Cookies.remove(this.cookieName)
        }
        // Pulisci token salvato
        window.api.clearSavedToken()
        // Reset completo stato
        this.$reset()
    }
    resetTotpFlow() {
        this.changeState({
            loginFlow: null,
            tempToken: null,
            setupToken: null,
            totpSecret: null,
            qrCodeDataUrl: null,
            backupCodes: null
        });
    }
    async logout(): Promise<void> {
        this.setLoading(true)

        try {
            // Chiama API logout (pulisce anche token server-side se necessario)
            await window.api.logout()
        } catch (error) {
            console.warn('Logout API call failed:', error)
            // Non bloccare il logout locale anche se la chiamata fallisce
        } finally {
            this.resetAuth()
        }
    }
    // ===== LOGIN CLASSICO =====

    async login(email: string, password: string): Promise<ApiResponse<LoginResponseData>> {
        this.setLoading(true)
        this.resetTotpFlow()

        try {
            const loginResponse = await window.api.login(email, password)

            if (!loginResponse.success) {
                this.setError(loginResponse.error || 'Login failed')
                return loginResponse
            }

            // Validazione risposta
            const validation = loginResponseDataSchema.safeParse(loginResponse.data)
            if (!validation.success) {
                console.error('Invalid login response format:', validation.error)
                this.setError('Invalid login response format')
                return apiError('Invalid login response format')
            }

            const loginData = validation.data

            // Gestisci i 3 possibili casi controllando i campi presenti
            if ('token' in loginData && 'user' in loginData && 'cookieName' in loginData) {
                // Login completato - utente già autenticato
                this.changeState({
                    user: loginData.user,
                    token: loginData.token,
                    cookieName: loginData.cookieName,
                    authState: 'authenticated',
                    loginFlow: null
                });
                this.setLoading(false)
                return apiSuccess(loginData, 'Login completato con successo')

            } else if ('requiresTotpSetup' in loginData && loginData.requiresTotpSetup) {
                // Richiede setup TOTP obbligatorio
                this.changeState({
                    setupToken: loginData.setupToken,
                    loginFlow: 'setup',
                    authState: 'pending_totp'
                });
                this.setLoading(false)
                return apiSuccess(loginData, 'Setup TOTP richiesto')

            } else if ('requiresTotpCode' in loginData && loginData.requiresTotpCode) {
                // Richiede codice TOTP per completare login
                this.changeState({
                    tempToken: loginData.tempToken,
                    loginFlow: 'code',
                    authState: 'pending_totp'
                });
                this.setLoading(false)
                return apiSuccess(loginData, 'Codice TOTP richiesto')

            } else {
                this.setError('Tipo di risposta login non riconosciuto')
                return apiError('Tipo di risposta login non riconosciuto')
            }

        } catch (error) {
            console.error('Login error:', error)
            this.setError('Errore durante il login')
            return apiError('Errore durante il login')
        }
    }
    // ===== SETUP TOTP =====

    async setupTotp(): Promise<ApiResponse<TotpSetupResponse>> {
        if (!this.setupToken) {
            this.setError('Setup token mancante')
            return apiError('Setup token mancante')
        }

        this.setLoading(true)

        try {
            const response = await window.api.setupTotp(this.setupToken)

            if (response.success && response.data) {
                // Salva dati temporanei per il setup
                this.changeState({
                    totpSecret: response.data.secret,
                    qrCodeDataUrl: response.data.qr_code_data_url,
                    backupCodes: response.data.backup_codes
                });
                this.setLoading(false)
                return response as ApiResponse<TotpSetupResponse>
            } else {
                this.setError(response.error || 'Errore durante setup TOTP')
                return response as ApiResponse<TotpSetupResponse>
            }

        } catch (error) {
            console.error('Setup TOTP error:', error)
            this.setError('Errore durante setup TOTP')
            return apiError('Errore durante setup TOTP')
        }
    }
    async completeTotpSetup(code: string): Promise<ApiResponse<TotpVerifyResponse>> {
        if (!this.setupToken) {
            this.setError('Setup token mancante')
            return apiError('Setup token mancante')
        }

        this.setLoading(true)

        try {
            const input: TotpVerifyInput = {
                setupToken: this.setupToken,
                code: code
            }

            const response = await window.api.verifyTotpSetup(input)

            if (response.success && response.data) {
                // La risposta del server ha la struttura: { success, data: { token, user, cookieName } }
                // dove response.data è di tipo TotpVerifyResponse
                const verifyData = response.data as TotpVerifyResponse;

                if (verifyData.token && verifyData.user) {
                    // Setup completato con successo - login automatico
                    console.log('Setup completato con successo - login automatico');
                    this.changeState({
                        user: verifyData.user,
                        token: verifyData.token,
                        cookieName: verifyData.cookieName || null,
                        authState: 'authenticated',
                        loginFlow: null,
                        setupToken: null,
                        tempToken: null,
                        totpSecret: null,
                        qrCodeDataUrl: null
                        // Mantieni backupCodes per permettere il download/salvataggio
                    });

                    this.setLoading(false)
                    return response
                } else {
                    this.setError('Risposta del server incompleta')
                    return response
                }
            } else {
                this.setError(response.error || 'Errore durante verifica setup')
                return response
            }

        } catch (error) {
            console.error('Complete TOTP setup error:', error)
            this.setError('Errore durante verifica setup')
            return apiError('Errore durante verifica setup')
        }
    }
    // ===== LOGIN CON TOTP =====
    async completeTotpLogin(totpCode: string): Promise<ApiResponse<LoginWithTotpResponse>> {
        if (!this.tempToken) {
            this.setError('Token temporaneo mancante')
            return apiError('Token temporaneo mancante')
        }

        this.setLoading(true)

        try {
            const input: LoginWithTotpInput = {
                tempToken: this.tempToken,
                totpCode: totpCode
            }

            const response = await window.api.loginWithTotp(input)

            if (response.success && response.data) {
                // Login completato con successo
                this.changeState({
                    user: response.data.user,
                    token: response.data.token,
                    cookieName: response.data.cookieName,
                    authState: 'authenticated'
                });

                // Reset flusso TOTP
                this.resetTotpFlow()

                this.setLoading(false)
                return response
            } else {
                this.setError(response.error || 'Codice TOTP non valido')
                return response
            }

        } catch (error) {
            console.error('Complete TOTP login error:', error)
            this.setError('Errore durante login con TOTP')
            return apiError('Errore durante login con TOTP')
        }
    }
    // ===== BACKUP CODES =====
    async regenerateBackupCodes(password: string): Promise<ApiResponse<RegenerateBackupCodesResponse>> {
        if (!this.isAuthenticated) {
            return apiError('Utente non autenticato')
        }

        this.setLoading(true)

        try {
            const input: RegenerateBackupCodesInput = { password }
            const response = await window.api.regenerateBackupCodes(input)

            if (response.success && response.data) {
                // Aggiorna backup codes temporanei per mostrarli all'utente
                this.changeState({
                    backupCodes: response.data.backup_codes
                });
                this.setLoading(false)
                return response
            } else {
                this.setError(response.error || 'Errore durante rigenerazione codici')
                return response
            }

        } catch (error) {
            console.error('Regenerate backup codes error:', error)
            this.setError('Errore durante rigenerazione codici')
            return apiError('Errore durante rigenerazione codici')
        }
    }
    // Pulisce i backup codes dopo che l'utente li ha salvati
    clearBackupCodes() {
        this.changeState({
            backupCodes: null
        });
    }

    // ===== VERIFICA TOKEN =====
    async verifyAuthentication(): Promise<boolean> {
        // Se abbiamo già un utente, consideriamo valida l'autenticazione
        if (this.isAuthenticated) {
            return true
        }

        // Controlla se c'è un token salvato
        const savedToken = window.api.getSavedToken()
        if (!savedToken) {
            this.changeState({
                authState: 'unauthenticated'
            });
            return false
        }

        this.setLoading(true)

        try {
            const response = await window.api.verifyToken()

            if (response.success && response.data?.user) {
                // Token valido - ripristina stato autenticato
                this.changeState({
                    user: response.data.user,
                    token: savedToken,
                    authState: 'authenticated'
                });
                this.setLoading(false)
                return true
            } else {
                // Token non valido - pulisci tutto
                this.resetAuth()
                return false
            }

        } catch (error) {
            console.error('Token verification error:', error)
            this.resetAuth()
            return false
        }
    }
    // ===== INIZIALIZZAZIONE =====

    async initialize(): Promise<void> {
        this.changeState({
            authState: 'loading'
        });

        try {
            const isValid = await this.verifyAuthentication()
            if (!isValid) {
                this.changeState({
                    authState: 'unauthenticated'
                });
            }
        } catch (error) {
            console.error('Auth initialization error:', error)
            this.changeState({
                authState: 'unauthenticated'
            });
        }
    }
    // ===== RESET PASSWORD =====

    /**
     * Richiesta reset password - invia email se email esiste
     */
    async requestPasswordReset(email: string): Promise<ApiResponse<ForgotPasswordResponse>> {
        this.setLoading(true)

        try {
            const response = await window.api.forgotPassword(email)
            this.setLoading(false)

            if (response.success) {
                // Non salviamo nessun stato per il reset password per sicurezza
                return response
            } else {
                this.setError(response.error || 'Errore durante richiesta reset password')
                return response
            }

        } catch (error) {
            console.error('Request password reset error:', error)
            this.setError('Errore durante richiesta reset password')
            return apiError('Errore durante richiesta reset password')
        }
    }
    /**
         * Completa reset password con token valido
         */
    async completePasswordReset(token: string, newPassword: string): Promise<ApiResponse<ResetPasswordResponse>> {
        this.setLoading(true)

        try {
            const response = await window.api.resetPassword(token, newPassword)
            this.setLoading(false)

            if (response.success) {
                // Reset completato con successo
                // L'utente dovrà rifare login con la nuova password
                return response
            } else {
                this.setError(response.error || 'Errore durante reset password')
                return response
            }

        } catch (error) {
            console.error('Complete password reset error:', error)
            this.setError('Errore durante reset password')
            return apiError('Errore durante reset password')
        }
    }
    /**
     * Verifica validità token di reset (per UI)
     */
    async verifyPasswordResetToken(token: string): Promise<ApiResponse<VerifyResetTokenResponse>> {
        this.setLoading(true)

        try {
            const response = await window.api.verifyResetToken(token)
            this.setLoading(false)

            return response

        } catch (error) {
            console.error('Verify reset token error:', error)
            this.setError('Errore durante verifica token')
            return apiError('Errore durante verifica token')
        }
    }
}
const authStore = AuthStore.getInstance();
export { authStore };